import React, { useEffect, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { range } from 'lodash';

const SuccessionBuyer = ({ query, updateCfaQuery, currentUser, intl }) => {
  const company = currentUser?.company;
  
  // Local state for percentage inputs to avoid reformatting during typing
  const [percentageInputs, setPercentageInputs] = useState({});
  
  // Initialize percentage inputs from query
  useEffect(() => {
    const newPercentageInputs = {};
    for (let i = 0; i < query.numberOfBuyers; i++) {
      const buyerIndex = i + 1;
      // Growth rate
      if (query[`buyer${buyerIndex}FiveYearGrowthRate`] !== undefined) {
        newPercentageInputs[`buyer${buyerIndex}GrowthRate`] = (query[`buyer${buyerIndex}FiveYearGrowthRate`] * 100).toFixed(1);
      }
      // Percentage purchasing
      if (query[`buyer${buyerIndex}PercentagePurchasing`] !== undefined) {
        newPercentageInputs[`buyer${buyerIndex}PercentagePurchasing`] = (query[`buyer${buyerIndex}PercentagePurchasing`] * 100).toFixed(1);
      }
      // Tax rate
      if (query[`buyer${buyerIndex}TaxRate`] !== undefined) {
        newPercentageInputs[`buyer${buyerIndex}TaxRate`] = (query[`buyer${buyerIndex}TaxRate`] * 100).toFixed(1);
      }
    }
    setPercentageInputs(newPercentageInputs);
  }, [query.numberOfBuyers]);
  
  const validate = () => {
    let valid = true;

    for (let i = 0; i < query.numberOfBuyers; i++) {
      if (query[`buyer${i + 1}T12Revenue`] == undefined || query[`buyer${i + 1}T12Revenue`] < 1) {
        valid = false;
      }

      if (!company?.host?.toString().includes("rjf.com")) {
        if (query[`buyer${i + 1}ManualTaxRate`] == undefined || (query[`buyer${i + 1}ManualTaxRate`] && parseFloat(query[`buyer${i + 1}TaxRate`] || 0) < 0.01)) {
          valid = false;
        }
      }
    }

    if (query.numberOfBuyers > 1 && (query[`buyer1PercentagePurchasing`] + query[`buyer2PercentagePurchasing`]) !== 1) {
      valid = false;
    }

    if (query.valid != valid) {
      updateCfaQuery({ ...query, valid });
    }
  };

  // Call validate on every render (matching original behavior)
  validate();

  const handleBuyerFieldChange = (buyerIndex, field, value) => {
    const updates = {
      [`buyer${buyerIndex + 1}${field}`]: value
    };
    updateCfaQuery({ ...query, ...updates });
  };

  // Set default buyer tax rates if not set
  useEffect(() => {
    const needsDefaults = {};
    for (let i = 0; i < query.numberOfBuyers; i++) {
      const buyerIndex = i + 1;
      if (query[`buyer${buyerIndex}TaxRate`] === undefined) {
        needsDefaults[`buyer${buyerIndex}TaxRate`] = 0.35;
      }
      if (query[`buyer${buyerIndex}FiveYearGrowthRate`] === undefined) {
        needsDefaults[`buyer${buyerIndex}FiveYearGrowthRate`] = 0;
      }
    }
    if (Object.keys(needsDefaults).length > 0) {
      updateCfaQuery({ ...query, ...needsDefaults });
    }
  }, [query.numberOfBuyers]);

  const handlePercentageChange = (key, displayValue, decimalField, buyerIndex) => {
    // Update local state for display
    setPercentageInputs(prev => ({
      ...prev,
      [key]: displayValue
    }));
    
    // Update query with decimal value if it's a valid number
    const numValue = parseFloat(displayValue);
    if (!isNaN(numValue)) {
      let maxValue = 100;
      
      // Special handling for percentage purchasing with multiple buyers
      if (decimalField === 'PercentagePurchasing' && query.numberOfBuyers > 1) {
        // Calculate how much percentage is available for this buyer
        const otherBuyerIndex = buyerIndex === 0 ? 1 : 0;
        const otherBuyerPercentage = (query[`buyer${otherBuyerIndex + 1}PercentagePurchasing`] || 0) * 100;
        maxValue = 100 - otherBuyerPercentage;
        maxValue = Math.max(0, maxValue); // Ensure it's not negative
      }
      
      const clampedValue = Math.max(0, Math.min(maxValue, numValue));
      const decimalValue = clampedValue / 100;
      handleBuyerFieldChange(buyerIndex, decimalField, decimalValue);
      
      // Update the display value to the clamped value if it was changed
      if (clampedValue !== numValue) {
        setPercentageInputs(prev => ({
          ...prev,
          [key]: clampedValue.toString()
        }));
      }
    }
  };

  const formatCurrency = (value) => {
    if (!value || value === '') return '';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatNumber = (value) => {
    if (!value || value === '') return '';
    return new Intl.NumberFormat('en-US').format(value);
  };

  const getMaxPercentageForBuyer = (buyerIndex, field) => {
    if (field !== 'PercentagePurchasing' || query.numberOfBuyers === 1) {
      return 100;
    }
    
    // For multiple buyers, calculate available percentage
    const otherBuyerIndex = buyerIndex === 0 ? 1 : 0;
    const otherBuyerPercentage = (query[`buyer${otherBuyerIndex + 1}PercentagePurchasing`] || 0) * 100;
    return Math.max(0, 100 - otherBuyerPercentage);
  };

  return (
    <div className="max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold text-gray-900 mb-4">
        <FormattedMessage id="calculators.cfa.tell_us_about" />
      </h2>
      <p className="text-gray-600 mb-8">
        <FormattedMessage id="calculators.cfa.buyer_copy" />
      </p>
      
      <div className="space-y-8">
        {range(0, query.numberOfBuyers).map((index) => (
          <div key={index} className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">
              <FormattedMessage id="calculators.cfa.buyer" /> {query.numberOfBuyers > 1 && (index + 1)}
            </h3>
            
            <div className="space-y-6">
              {/* T12 Revenue */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FormattedMessage id="calculators.cfa.buyers_t12_revenue" values={{ index: index + 1 }} />
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <div className="relative max-w-xs">
                  <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500">$</span>
                  <input
                    type="text"
                    value={formatNumber(query[`buyer${index + 1}T12Revenue`]) || ''}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9.]/g, '');
                      handleBuyerFieldChange(index, 'T12Revenue', value ? parseFloat(value) : undefined);
                    }}
                    className="block w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-right"
                    placeholder="0"
                  />
                </div>
              </div>

              {/* Growth Rate (not for RJF) */}
              {!company?.host?.toString().includes("rjf.com") && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FormattedMessage id="calculators.cfa.buyers_growth_rate" values={{ index: index + 1 }} />
                  </label>
                  <div className="relative max-w-32">
                    <input
                      type="number"
                      min="-100"
                      max="300"
                      step="0.1"
                      value={percentageInputs[`buyer${index + 1}GrowthRate`] || ''}
                      onChange={(e) => {
                        handlePercentageChange(
                          `buyer${index + 1}GrowthRate`,
                          e.target.value,
                          'FiveYearGrowthRate',
                          index
                        );
                      }}
                      onFocus={(e) => e.target.select()}
                      className="block w-full pr-8 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <span className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500">%</span>
                  </div>
                </div>
              )}

              {/* Percentage Purchasing (only for multiple buyers) */}
              {query.numberOfBuyers > 1 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FormattedMessage id="calculators.cfa.percent_purchased" />
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <div className="relative max-w-32">
                    <input
                      type="number"
                      min="0"
                      max={getMaxPercentageForBuyer(index, 'PercentagePurchasing')}
                      step="0.1"
                      value={percentageInputs[`buyer${index + 1}PercentagePurchasing`] || ''}
                      onChange={(e) => {
                        handlePercentageChange(
                          `buyer${index + 1}PercentagePurchasing`,
                          e.target.value,
                          'PercentagePurchasing',
                          index
                        );
                      }}
                      onFocus={(e) => e.target.select()}
                      className="block w-full pr-8 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <span className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500">%</span>
                  </div>
                  <div className="mt-1 text-xs text-gray-500">
                    {intl.formatMessage({ id: 'succession.cash_flow_analysis.buyer.available_pct' }, { pct: getMaxPercentageForBuyer(index, 'PercentagePurchasing').toFixed(1) })}
                  </div>
                </div>
              )}

              {/* Tax Rate Override (not for RJF) */}
              {!company?.host?.toString().includes("rjf.com") && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FormattedMessage id="calculators.cfa.manually_override_tax_rate" />
                  </label>
                  <div className="space-y-3">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name={`buyer${index + 1}TaxRate`}
                        checked={!query[`buyer${index + 1}ManualTaxRate`]}
                        onChange={() => {
                          updateCfaQuery({ 
                            ...query, 
                            [`buyer${index + 1}ManualTaxRate`]: false 
                          });
                        }}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        <FormattedMessage id="calculators.cfa.dynamic" />
                      </span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name={`buyer${index + 1}TaxRate`}
                        checked={query[`buyer${index + 1}ManualTaxRate`]}
                        onChange={() => {
                          const taxRate = query[`buyer${index + 1}TaxRate`] !== undefined ? query[`buyer${index + 1}TaxRate`] : 0.35;
                          updateCfaQuery({ 
                            ...query, 
                            [`buyer${index + 1}ManualTaxRate`]: true,
                            [`buyer${index + 1}TaxRate`]: taxRate
                          });
                        }}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        <FormattedMessage id="calculators.cfa.manual" />
                      </span>
                    </label>
                  </div>

                  {query[`buyer${index + 1}ManualTaxRate`] && (
                    <div className="mt-3">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <FormattedMessage id="calculators.cfa.tax_rate" />
                      </label>
                      <div className="relative max-w-32">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          value={percentageInputs[`buyer${index + 1}TaxRate`] || ''}
                          onChange={(e) => {
                            handlePercentageChange(
                              `buyer${index + 1}TaxRate`,
                              e.target.value,
                              'TaxRate',
                              index
                            );
                          }}
                          onFocus={(e) => e.target.select()}
                          className="block w-full pr-8 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        <span className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500">%</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}

        {/* Validation Messages */}
        {query.numberOfBuyers > 1 && (() => {
          const totalPercentage = (query.buyer1PercentagePurchasing || 0) + (query.buyer2PercentagePurchasing || 0);
          const totalPercentageDisplay = (totalPercentage * 100).toFixed(1);
          const isComplete = Math.abs(totalPercentage - 1) < 0.001; // Account for floating point precision
          
          return (
            <div className={`border rounded-lg p-4 ${isComplete ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200'}`}>
              <div className="flex">
                <div className="flex-shrink-0">
                  {isComplete ? (
                    <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
                <div className="ml-3">
                  <p className={`text-sm ${isComplete ? 'text-green-800' : 'text-blue-800'}`}>
                    {isComplete
                      ? intl.formatMessage({ id: 'succession.cash_flow_analysis.buyer.perfect_total' }, { pct: totalPercentageDisplay })
                      : intl.formatMessage({ id: 'succession.cash_flow_analysis.buyer.current_total' }, { pct: totalPercentageDisplay })
                    }
                  </p>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default injectIntl(SuccessionBuyer); 